/**
 * Phase 15.2 — persist @tanstack/react-query cache в IndexedDB.
 *
 * Зачем: Phase 15.1 персистит только chatStore (каналы, seq-курсоры,
 * последние сообщения). Всё остальное — друзья, shop-каталог, профили,
 * мини-аппы — живёт в памяти React Query и теряется при reload.
 *
 * Реализация без зависимости `@tanstack/react-query-persist-client`:
 *   — на старте читаем снапшот из IDB и скармливаем `hydrate()`;
 *   — подписываемся на cache events, debounce 500мс и сохраняем `dehydrate()`.
 *   — snapshot живёт 24 часа; при logout чистится через `clearIdbCache()`.
 *
 * Мы не кэшируем мутации, только queries. queries с ошибкой тоже
 * не сохраняются (по умолчанию `defaultShouldDehydrateQuery`).
 */

import type { QueryClient, DehydratedState } from '@tanstack/react-query';
import { dehydrate, hydrate } from '@tanstack/react-query';
import { idbStorage, clearIdbCache } from './idbStorage';

const KEY = 'rq-snapshot';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h
// Phase 15.3 — хардкап на размер сериализованного snapshot'а. Страхует от
// редких случаев когда у юзера распухшая история (много чатов × много
// сообщений × много реакций). При превышении — пропускаем запись; query-cache
// продолжает жить в RAM, теряется только persist между reload'ами.
const MAX_SNAPSHOT_BYTES = 5 * 1024 * 1024; // 5 MB

interface Snapshot {
  ts: number;
  userId: string | null;
  state: DehydratedState;
}

/**
 * Phase 15.3 — гидрация с проверкой "чей это снепшот". Если у нас в
 * localStorage/Zustand уже другой user.id (смена аккаунта на общем
 * девайсе) — чужой кэш НЕ грузим и чистим IDB. Это защищает от сценария
 * "Маша залогинилась после Пети и увидела его каналы".
 */
export async function hydrateQueryClient(client: QueryClient, currentUserId: string | null): Promise<void> {
  const raw = await idbStorage.getItem(KEY);
  if (!raw) return;
  try {
    const snap = JSON.parse(raw) as Snapshot;
    if (Date.now() - snap.ts > MAX_AGE_MS) return;
    // User mismatch — сбрасываем всё. Без currentUserId (юзер не залогинен) —
    // гидрируем безопасно, там только public shop-каталог и т.п.
    if (currentUserId && snap.userId && snap.userId !== currentUserId) {
      await clearIdbCache();
      return;
    }
    hydrate(client, snap.state);
    // v5.7.8 — после hydrate'а messages-query из snapshot'а помечаем stale,
    // чтобы при mount'е компонента ChatScreen React Query сделал refetch
    // и юзер не залипал на дневной snapshot'е без свежих сообщений.
    // hydrate() сохраняет original `dataUpdatedAt` из времени dehydrate;
    // staleTime=30s default → query считает себя stale, НО refetchOnMount
    // не triggers если компонент был mounted до hydrate (race).
    // invalidateQueries — public API React Query — корректно notifies
    // observers и triggers refetch на следующем mount/focus. Не очищает
    // cache (UX-benefit мгновенного рендера сохранён, fresh fetch в фоне).
    // Делаем только для 'messages' — chat history наиболее sensitive
    // к свежести. Остальные queries (друзья, shop, профили) безопасно
    // живут со staleTime=30s.
    //
    // v5.7.14 — default `refetchType:'active'` пропускает inactive queries.
    // После re-login query ещё не active в момент invalidate → refetch
    // skipped → admin видит stale messages. `refetchType:'all'` гарантирует
    // refetch при следующем mount/access, независимо от active state.
    client.invalidateQueries({ queryKey: ['messages'], refetchType: 'all' });
  } catch {
    // повреждённый снимок — проигнорируем
  }
}

export function attachQueryPersist(client: QueryClient, getUserId: () => string | null): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    timer = null;
    try {
      const state = dehydrate(client);
      const snap: Snapshot = { ts: Date.now(), userId: getUserId(), state };
      const serialized = JSON.stringify(snap);
      if (serialized.length > MAX_SNAPSHOT_BYTES) {
        // Слишком большой снапшот — лучше пропустить, чем потом на load
        // зависать на десериализации или упереться в IDB quota.
        return;
      }
      void idbStorage.setItem(KEY, serialized);
    } catch {
      // JSON может не сериализовать какие-то данные — пропускаем
    }
  };

  const schedule = () => {
    if (timer) return;
    timer = setTimeout(flush, 500);
  };

  // Подписываемся на оба кэша: queries и (опц.) mutations.
  const unsubQ = client.getQueryCache().subscribe(schedule);
  const unsubM = client.getMutationCache().subscribe(schedule);

  return () => {
    unsubQ();
    unsubM();
    if (timer) clearTimeout(timer);
  };
}
